import { z } from 'zod';

import { Box, Callout, Form, Stack } from '@ekhodealer/ekho-common/rev';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';

import { useGetAiBdcSettings } from '../api/useGetAiBdcSettings';
import { useUpdateAiBdcSettings } from '../api/useUpdateAiBdcSettings';

// --- Text off-ramp ---

const textOffRampSchema = z.object({
	enableTextOffRamp: z.boolean().optional(),
});

type TextOffRampFormData = z.infer<typeof textOffRampSchema>;

function TextOffRampForm() {
	const spId = useCurrentServicesProviderId();
	const { data: settings } = useGetAiBdcSettings(spId);
	const updateMutation = useUpdateAiBdcSettings(spId);

	const isSmsEnabled = settings?.sms?.enabled === true;

	const handleSubmit = async (
		data: TextOffRampFormData,
	): Promise<{ isSuccess: boolean }> => {
		try {
			await updateMutation.mutateAsync({
				embeddedWebsiteAssistant: {
					...settings?.embeddedWebsiteAssistant,
					enableTextOffRamp: data.enableTextOffRamp,
				},
			});
			return { isSuccess: true };
		} catch {
			return { isSuccess: false };
		}
	};

	if (!isSmsEnabled) {
		return (
			<Box padding='24' background='secondary' rounding='12'>
				<div data-settings-disabled>
					<Form
						name='ai-bdc-text-off-ramp'
						schema={textOffRampSchema}
						defaultValues={{ enableTextOffRamp: false }}
						onSubmit={async () => ({ isSuccess: false })}
						title='Text input off-ramp'
						titleLevel='subgroup'
						titleWeight='semibold'
						titleMaxWidth='full'
						description='Offer visitors the option to continue the conversation via text message instead of chat.'
						toggle={{ fieldName: 'enableTextOffRamp' }}>
						{null}
					</Form>
				</div>
				<Callout variant='information'>
					Enable the SMS channel under General &gt; Channels to use the text off-ramp.
				</Callout>
			</Box>
		);
	}

	return (
		<Box padding='24' background='secondary' rounding='12'>
			<Form
				name='ai-bdc-text-off-ramp'
				schema={textOffRampSchema}
				defaultValues={{
					enableTextOffRamp: settings?.embeddedWebsiteAssistant?.enableTextOffRamp ?? false,
				}}
				onSubmit={handleSubmit}
				title='Text input off-ramp'
				titleLevel='subgroup'
				titleWeight='semibold'
				description='Offer visitors the option to continue the conversation via text message instead of chat.'
				tag={{ children: 'Recommended', accent: 1, appearance: 'solid', size: 'small' }}
				toggle={{ fieldName: 'enableTextOffRamp' }}
				showSaveAndClear>
				{null}
			</Form>
		</Box>
	);
}

// --- Suggestions ---

const suggestionsSchema = z.object({
	suggestion1: z.string().min(1, 'At least one suggestion is required').max(80, 'Max 80 characters'),
	suggestion2: z.string().min(1, 'Suggestion is required').max(80, 'Max 80 characters'),
	suggestion3: z.string().min(1, 'Suggestion is required').max(80, 'Max 80 characters'),
	suggestion4: z.string().min(1, 'Suggestion is required').max(80, 'Max 80 characters'),
});

function SuggestionsForm() {
	const spId = useCurrentServicesProviderId();
	const { data: settings } = useGetAiBdcSettings(spId);
	const updateMutation = useUpdateAiBdcSettings(spId);

	const suggestions = settings?.embeddedWebsiteAssistant?.suggestions ?? [];

	const handleSubmit = async (
		data: z.infer<typeof suggestionsSchema>,
	): Promise<{ isSuccess: boolean }> => {
		try {
			const items = [
				data.suggestion1,
				data.suggestion2,
				data.suggestion3,
				data.suggestion4,
			].filter(Boolean);

			await updateMutation.mutateAsync({
				embeddedWebsiteAssistant: {
					...settings?.embeddedWebsiteAssistant,
					suggestions: items.length > 0 ? items : undefined,
				},
			});
			return { isSuccess: true };
		} catch {
			return { isSuccess: false };
		}
	};

	return (
		<Box padding='24' background='secondary' rounding='12'>
			<Form
				name='ai-bdc-suggestions'
				schema={suggestionsSchema}
				defaultValues={{
					suggestion1: suggestions[0] ?? '',
					suggestion2: suggestions[1] ?? '',
					suggestion3: suggestions[2] ?? '',
					suggestion4: suggestions[3] ?? '',
				}}
				onSubmit={handleSubmit}
				showSaveAndClear
				title='Suggestions'
				titleLevel='subgroup'
				titleWeight='semibold'
				description='Clickable prompts shown to visitors when the chat opens. Choose short, action-oriented phrases that help buyers get started quickly — e.g., vehicle questions, financing, or scheduling a visit.'>
				<Stack itemsSpacing='8'>
					<Form.Field.Text
						schemaField='suggestion1'
						labelText=''
						required
						size='large'
						placeholder='E.g., What vehicles do you have in stock?'
						maxLength={80}
					/>
					<Form.Field.Text
						schemaField='suggestion2'
						labelText=''
						required
						size='large'
						placeholder='E.g., Tell me about financing options'
						maxLength={80}
					/>
					<Form.Field.Text
						schemaField='suggestion3'
						labelText=''
						required
						size='large'
						placeholder='E.g., I want to book a test drive'
						maxLength={80}
					/>
					<Form.Field.Text
						schemaField='suggestion4'
						labelText=''
						required
						size='large'
						placeholder='E.g., Do you offer trade-ins?'
						maxLength={80}
					/>
				</Stack>
			</Form>
		</Box>
	);
}

// --- Input placeholders ---

const placeholdersSchema = z.object({
	placeholder1: z.string().max(60, 'Max 60 characters').default('Ask any question...'),
	placeholder2: z.string().max(60, 'Max 60 characters').optional().or(z.literal('')),
	placeholder3: z.string().max(60, 'Max 60 characters').optional().or(z.literal('')),
	placeholder4: z.string().max(60, 'Max 60 characters').optional().or(z.literal('')),
});

function InputPlaceholdersForm() {
	const spId = useCurrentServicesProviderId();
	const { data: settings } = useGetAiBdcSettings(spId);
	const updateMutation = useUpdateAiBdcSettings(spId);

	const placeholders = settings?.embeddedWebsiteAssistant?.inputPlaceholders ?? [];

	const handleSubmit = async (
		data: z.infer<typeof placeholdersSchema>,
	): Promise<{ isSuccess: boolean }> => {
		try {
			const items = [
				data.placeholder1,
				data.placeholder2,
				data.placeholder3,
				data.placeholder4,
			].filter((s): s is string => Boolean(s));

			await updateMutation.mutateAsync({
				embeddedWebsiteAssistant: {
					...settings?.embeddedWebsiteAssistant,
					inputPlaceholders: items.length > 0 ? items : undefined,
				},
			});
			return { isSuccess: true };
		} catch {
			return { isSuccess: false };
		}
	};

	return (
		<Box padding='24' background='secondary' rounding='12'>
			<Form
				name='ai-bdc-input-placeholders'
				schema={placeholdersSchema}
				defaultValues={{
					placeholder1: placeholders[0] ?? 'Ask any question...',
					placeholder2: placeholders[1] ?? '',
					placeholder3: placeholders[2] ?? '',
					placeholder4: placeholders[3] ?? '',
				}}
				onSubmit={handleSubmit}
				showSaveAndClear
				title='Input placeholders'
				titleLevel='subgroup'
				titleWeight='semibold'
				description='Rotating placeholder text shown in the chat input field. The first placeholder is always shown by default.'>
				<Stack itemsSpacing='8'>
					<Form.Field.Text
						schemaField='placeholder1'
						labelText=''
						required
						size='large'
						placeholder='Ask any question...'
						isDisabled
						maxLength={60}
					/>
					<Form.Field.Text
						schemaField='placeholder2'
						labelText=''
						size='large'
						placeholder='E.g., Looking for a specific vehicle?'
						maxLength={60}
					/>
					<Form.Field.Text
						schemaField='placeholder3'
						labelText=''
						size='large'
						placeholder='E.g., How can I help you today?'
						maxLength={60}
					/>
					<Form.Field.Text
						schemaField='placeholder4'
						labelText=''
						size='large'
						placeholder='E.g., Ask about our latest deals...'
						maxLength={60}
					/>
				</Stack>
			</Form>
		</Box>
	);
}

// --- Quick action pills ---

const DEFAULT_PILLS = [
	{
		title: 'Sales',
		prompt: "I'm interested in learning about your vehicles and current offers.",
	},
	{ title: 'Service', prompt: "I'd like to schedule a service appointment." },
	{ title: 'Parts', prompt: "I'm looking for parts or accessories." },
];

const quickActionPillsSchema = z.object({
	pills: z
		.array(
			z.object({
				title: z.string().min(1, 'Title is required').max(20, 'Max 20 characters'),
				prompt: z.string().min(1, 'Prompt is required').max(200, 'Max 200 characters'),
			}),
		)
		.min(3, 'At least 3 pills are required'),
});

function QuickActionPillsForm() {
	const spId = useCurrentServicesProviderId();
	const { data: settings } = useGetAiBdcSettings(spId);
	const updateMutation = useUpdateAiBdcSettings(spId);

	const pills = settings?.embeddedWebsiteAssistant?.quickActionPills ?? DEFAULT_PILLS;

	const handleSubmit = async (
		data: z.infer<typeof quickActionPillsSchema>,
	): Promise<{ isSuccess: boolean }> => {
		try {
			const items = data.pills
				.filter((p) => p.title.trim() && p.prompt.trim())
				.map((p) => ({ title: p.title.trim(), prompt: p.prompt.trim() }));

			await updateMutation.mutateAsync({
				embeddedWebsiteAssistant: {
					...settings?.embeddedWebsiteAssistant,
					quickActionPills: items.length > 0 ? items : undefined,
				},
			});
			return { isSuccess: true };
		} catch {
			return { isSuccess: false };
		}
	};

	return (
		<Box padding='24' background='secondary' rounding='12'>
			<Form
				name='ai-bdc-quick-action-pills'
				schema={quickActionPillsSchema}
				defaultValues={{
					pills: pills.map((p) => ({ title: p.title, prompt: p.prompt })),
				}}
				onSubmit={handleSubmit}
				showSaveAndClear
				title='Quick action pills'
				titleLevel='subgroup'
				titleWeight='semibold'
				description='Shortcut pills displayed directly below the chat input on your website. Visitors can tap a pill to instantly start a conversation about that topic. You must configure at least three and can add up to five.'>
				<Form.Field.ObjectArray
					schemaField='pills'
					entryLimit={5}
					fields={[
						{
							key: 'title',
							label: 'Label',
							type: 'text',
							placeholder: 'E.g., Sales',
							required: true,
							maxLength: 20,
						},
						{
							key: 'prompt',
							label: 'Prompt',
							type: 'textarea',
							placeholder: "E.g., I'm interested in learning about your vehicles.",
							required: true,
							maxLength: 200,
						},
					]}
				/>
			</Form>
		</Box>
	);
}

// --- Help button ---

const helpButtonSchema = z.object({
	showHelpButton: z.boolean().optional(),
});

function HelpButtonForm() {
	const spId = useCurrentServicesProviderId();
	const { data: settings } = useGetAiBdcSettings(spId);
	const updateMutation = useUpdateAiBdcSettings(spId);

	const handleSubmit = async (
		data: z.infer<typeof helpButtonSchema>,
	): Promise<{ isSuccess: boolean }> => {
		try {
			await updateMutation.mutateAsync({
				embeddedWebsiteAssistant: {
					...settings?.embeddedWebsiteAssistant,
					showHelpButton: data.showHelpButton,
				},
			});
			return { isSuccess: true };
		} catch {
			return { isSuccess: false };
		}
	};

	return (
		<Box padding='24' background='secondary' rounding='12'>
			<Form
				name='ai-bdc-help-button'
				schema={helpButtonSchema}
				defaultValues={{
					showHelpButton: settings?.embeddedWebsiteAssistant?.showHelpButton ?? false,
				}}
				onSubmit={handleSubmit}
				title='Help button'
				titleLevel='subgroup'
				titleWeight='semibold'
				description="Display a 'Contact support' button on the chat widget so visitors can email your team directly."
				toggle={{ fieldName: 'showHelpButton' }}
				showSaveAndClear>
				{null}
			</Form>
		</Box>
	);
}

// --- Combined export ---

export function ChatExperienceSection() {
	return (
		<Stack itemsSpacing='16'>
			<QuickActionPillsForm />
			<TextOffRampForm />
			<SuggestionsForm />
			<InputPlaceholdersForm />
			<HelpButtonForm />
		</Stack>
	);
}
